import { useEffect, useState } from "react";
import { getArtLabelSuggestions, getArtLabels } from "../artLabels/functions";
import { statusLabel } from "../artLabels/status";
import { extendedArtErrorMessage } from "./functions";
import { validateArtFile } from "./uploadArtSource";

export const SOURCE_TABS = [
    { key: "suggestions", label: "Sugestões" },
    { key: "library", label: "Biblioteca" },
    { key: "upload", label: "Enviar imagem da carta" },
];

/** Com o TCGdex fora, as outras abas seguem valendo. */
const SEARCH_DOWN_HINT = "Use a Biblioteca ou envie a imagem da carta.";

const tileClass = (selected) =>
    `flex w-36 flex-col items-center gap-1 rounded border p-1 text-xs ${selected ? "border-primary bg-mgs-elevated" : "border-primary/30"}`;

export const modelsLabel = (count) => `${count} ${count === 1 ? "modelo" : "modelos"}`;

/** Origem da arte quando o modelo ainda não tem uma (ou o operador quer trocar) — spec §5.2. */
const ArtSourceTabs = ({ user, model, choice, onChoose }) => {
    const [tab, setTab] = useState("suggestions");
    const [suggestions, setSuggestions] = useState(null);
    const [suggestionError, setSuggestionError] = useState("");
    const [q, setQ] = useState(model?.name || "");
    const [library, setLibrary] = useState(null);
    const [libraryError, setLibraryError] = useState("");
    const [file, setFile] = useState(null);
    const [cardName, setCardName] = useState(model?.name || "");
    const [fileError, setFileError] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            const out = await getArtLabelSuggestions(user?.token, model._id);
            if (!alive) return;
            if (out?.type === "success") {
                setSuggestions(out.data?.items || []);
                setSuggestionError("");
            } else {
                setSuggestions([]);
                const message = extendedArtErrorMessage(out?.code, out?.message || "Falha ao buscar sugestões");
                setSuggestionError(out?.code === "CARD_SEARCH_UNAVAILABLE" ? `${message} ${SEARCH_DOWN_HINT}` : message);
            }
        })();
        return () => {
            alive = false;
        };
    }, [user?.token, model._id]);

    const searchLibrary = async () => {
        const out = await getArtLabels(user?.token, { q: q.trim(), limit: 24 });
        if (out?.type === "success") {
            setLibrary(out.data?.items || []);
            setLibraryError("");
        } else {
            setLibrary([]);
            setLibraryError(extendedArtErrorMessage(out?.code, out?.message || "Falha ao buscar na biblioteca"));
        }
    };

    const chooseUpload = (nextFile, nextName) => {
        const error = nextFile ? validateArtFile(nextFile) : null;
        setFileError(error || "");
        if (!nextFile || error || !nextName.trim()) {
            onChoose(null);
            return;
        }
        onChoose({ type: "upload", file: nextFile, cardName: nextName.trim(), label: `${nextFile.name} (${nextName.trim()})` });
    };

    return (
        <div>
            <div role="tablist" className="mb-2 flex flex-wrap gap-2">
                {SOURCE_TABS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={tab === t.key}
                        onClick={() => setTab(t.key)}
                        className={`rounded px-3 py-1 text-sm ${tab === t.key ? "bg-primary text-black" : "border border-primary/40 text-laranjinha"}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "suggestions" ? (
                <div>
                    {suggestionError ? <p className="text-sm text-red-400">{suggestionError}</p> : null}
                    {suggestions === null ? <p className="text-sm text-laranjinha/60">Carregando sugestões…</p> : null}
                    {suggestions?.length === 0 && !suggestionError ? <p className="text-sm text-laranjinha/60">Nenhuma carta encontrada para este modelo.</p> : null}
                    <div className="flex flex-wrap gap-2">
                        {(suggestions || []).map((s) => {
                            const label = [s.name, [s.setName, s.number].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
                            const selected = choice?.type === "tcgdex" && choice.cardId === s.cardId && choice.lang === s.lang;
                            return (
                                <button
                                    key={`${s.cardId}-${s.lang}`}
                                    type="button"
                                    aria-pressed={selected}
                                    aria-label={label}
                                    className={tileClass(selected)}
                                    onClick={() => onChoose({ type: "tcgdex", cardId: s.cardId, lang: s.lang, label })}
                                >
                                    {s.imageUrl ? <img alt="" src={s.imageUrl} className="h-28" /> : null}
                                    <span>{s.name}</span>
                                    <span className="text-laranjinha/60">{[s.setName, s.number].filter(Boolean).join(" ")}</span>
                                    {s.artStatus ? <span className="text-primary">arte: {statusLabel(s.artStatus)}</span> : null}
                                </button>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            {tab === "library" ? (
                <div>
                    <div className="mb-2 flex gap-2">
                        <input aria-label="Buscar na biblioteca" className="px-2 text-black" value={q} onChange={(e) => setQ(e.target.value)} />
                        <button type="button" onClick={searchLibrary} className="rounded border border-primary/40 px-3 text-sm">Buscar</button>
                    </div>
                    {libraryError ? <p className="text-sm text-red-400">{libraryError}</p> : null}
                    {library?.length === 0 && !libraryError ? <p className="text-sm text-laranjinha/60">Nenhuma arte encontrada.</p> : null}
                    <div className="flex flex-wrap gap-2">
                        {(library || []).map((a) => {
                            const label = `${a.cardName} (${a.artKey})`;
                            const selected = choice?.type === "library" && choice.artKey === a.artKey;
                            // GET /admin/art-labels devolve o documento cru: não há `thumbUrl` derivado (D19).
                            const thumb = a.thumbUrl ?? a.sourceImage?.url;
                            return (
                                <button
                                    key={a.artKey}
                                    type="button"
                                    aria-pressed={selected}
                                    aria-label={label}
                                    className={tileClass(selected)}
                                    onClick={() => onChoose({ type: "library", artKey: a.artKey, label })}
                                >
                                    {thumb ? <img alt="" src={thumb} className="h-20" /> : null}
                                    <span>{a.cardName}</span>
                                    <span className="text-laranjinha/60">{statusLabel(a.status)} · {modelsLabel((a.gradableModels || []).length)}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            ) : null}

            {tab === "upload" ? (
                <div className="flex flex-col gap-2 text-sm">
                    <label className="flex flex-col gap-1">
                        Imagem da carta (PNG ou JPEG, até 10 MB)
                        <input
                            type="file"
                            accept="image/png,image/jpeg"
                            onChange={(e) => {
                                const next = e.target.files?.[0] || null;
                                setFile(next);
                                chooseUpload(next, cardName);
                            }}
                        />
                    </label>
                    {fileError ? <p className="text-red-400">{fileError}</p> : null}
                    <label className="flex flex-col gap-1">
                        Nome da carta
                        <input
                            className="px-2 text-black"
                            value={cardName}
                            onChange={(e) => {
                                setCardName(e.target.value);
                                if (file) chooseUpload(file, e.target.value);
                            }}
                        />
                    </label>
                </div>
            ) : null}
        </div>
    );
};

export default ArtSourceTabs;
